/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonSpinner, IonToast, useIonToast } from '@ionic/react';
import { EmptyPollOption, FullPoll, FullPost, Poll, PollOption, Post, objectEquals } from 'model';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDeletePostMutation, useGetPostQuery, useUpdatePostMutation } from '../api/PostApi';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { PollEditFields } from '../components/PollEditFields';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { PollEditRules } from '../formConfigs/EditPollFieldsConfig';
import { useFormValues } from '../hooks/useFormValues';

import { v4 as uuidv4 } from 'uuid';
import FormFooter from '../components/FormFooter';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { updateKeyInPollOptions } from '../util/updateKeyInPollOptions';
export const startingPollOptions: PollOption[] = [
	{
		value: '',
		key: uuidv4(),
		order: 0
	},
	{
		value: '',
		key: uuidv4(),
		order: 0
	}
];

export const EditPoll: React.FC = () => {
	const { postid } = useParams<{ postid: Post['_id'] }>();
	const { data: fetchPoll } = useGetPostQuery(postid, { skip: !postid }) as { data: FullPoll };
	const [poll, setPoll] = useState<FullPost | undefined | null>(fetchPoll);
	const [pollOptions, setPollOptions] = useState<PollOption[]>(startingPollOptions);
	const [optionErrors, setOptionErrors] = useState<string[]>([]);
	const history = useHistory();
	const [deleteItem] = useDeletePostMutation();
	const [updatePost, { isError: updateIsError, isLoading: updateIsLoading }] = useUpdatePostMutation();
	const {
		setValues,
		formSuccess,
		formValues,
		validateForm,
		stateDataObject: newPoll
	} = useFormValues(PollEditRules, poll as Poll, objectEquals);

	const [showToast] = useIonToast();

	const onSubmit = useCallback(async () => {
		const compiledNoErrors = validateForm();
		if (compiledNoErrors && formValues && newPoll) {
			const newOptions: PollOption[] = [...pollOptions];
			const deletedEmptyOptions: PollOption[] = newOptions.filter(option => option.value !== '');
			const newPollWithoutEmpties = { ...newPoll, options: [...deletedEmptyOptions] };

			if (poll) {
				const result = await updatePost({
					post: {
						...poll,
						...newPollWithoutEmpties,
						draft: false
					}
				});
				if (!('error' in result)) {
					setPoll(result.data);
					showToast({
						color: 'success',
						message: 'Poll has been updated',
						position: 'top',
						duration: 1500
					});
					history.push('/main/latest');
				} else {
					showToast({
						color: 'danger',
						message: 'Error updating poll',
						position: 'top',
						duration: 1500
					});
				}
			}
		}
	}, [formValues, history, newPoll, poll, pollOptions, showToast, updatePost, validateForm]);

	const validateFirstTwoPollOptions = (pollOptionsToValidate: PollOption[]) => {
		const updatedOptionErrors: string[] = [];
		if (pollOptionsToValidate.slice(0, 2).some(option => option.value.length === 0)) {
			updatedOptionErrors.push('Please complete at least two options');
		}
		if (pollOptionsToValidate.some(option => option.value.length > 120)) {
			updatedOptionErrors.push('Options should be less than 120 characters');
		}

		return updatedOptionErrors;
	};

	const setPollValues = useCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			const updatedPollOptions: PollOption[] = updateKeyInPollOptions(pollOptions, e.target.name, e.target.value);

			setPollOptions(updatedPollOptions);
			setValues({ options: updatedPollOptions });
			setOptionErrors(validateFirstTwoPollOptions(updatedPollOptions));
		},
		[pollOptions, setValues]
	);

	const onCancel = useCallback(async () => {
		history.go(-1);
		if (fetchPoll && fetchPoll.draft) {
			await deleteItem(fetchPoll._id);
		}
	}, [deleteItem, fetchPoll, history]);

	const addOption = useCallback(() => {
		const newOptions: PollOption[] = [...pollOptions];
		newOptions.push({ ...EmptyPollOption, key: uuidv4() });
		setPollOptions(newOptions);
	}, [pollOptions]);

	const deleteOption = useCallback(
		(value: string) => {
			const newOptions: PollOption[] = [...pollOptions];
			const deletedOptions: PollOption[] = newOptions.filter(option => option.key !== value);
			setPollOptions(deletedOptions);
			setValues({ options: deletedOptions });
		},
		[pollOptions, setValues]
	);

	useEffect(() => {
		if (fetchPoll) {
			setPoll(fetchPoll);
			fetchPoll.options?.length > 0 && setPollOptions([...fetchPoll.options]);
		}
		return () => setPoll(undefined);
	}, [setPoll, fetchPoll]);

	return (
		<QuirkIonPage name="Edit Poll" data={{ poll: postid }}>
			<Header
				title="Edit Poll"
				left={<IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					<IconButton
						label="Done"
						fill="solid"
						disabled={
							!formSuccess ||
							validateFirstTwoPollOptions(pollOptions).length > 0 ||
							optionErrors.length > 0
						}
						icon={<IconCheck />}
						onClick={onSubmit}
						testId="editPostDoneButton"
					/>
				}
			/>
			<QuirkIonContent flush padding>
				<div>
					{poll && formValues && (
						<PollEditFields
							setValues={setValues}
							addOption={addOption}
							deleteOption={deleteOption}
							optionErrors={optionErrors}
							setPollValues={setPollValues}
							setEvent={setPoll}
							formValues={formValues}
							poll={poll}
							pollOptions={pollOptions}
						/>
					)}
					{poll && formValues && (
						<FormFooter
							onCancel={onCancel}
							onSubmit={onSubmit}
							disableSubmit={
								!formSuccess ||
								validateFirstTwoPollOptions(pollOptions).length > 0 ||
								optionErrors.length > 0
							}
						/>
					)}
					<IonSpinner name="crescent" hidden={!updateIsLoading} />
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message={(updateIsError as any)?.data}
						position="top"
						color="danger"
					/>
				</div>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
